import React, { useEffect, useState } from 'react';
import '../auth/forgot.css';

// Legacy-only unified forgot password component (Email/SMS -> OTP + reset) without normalization.
// Endpoints used:
//   POST /api/usr-forgot-password { type: 'email'|'sms', email|phone, username }
//   POST /api/usr-reset-password  { otp, password, password_confirmation, username }

const PASSWORD_REGEX_LEGACY = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#]{8,}$/;

export default function ForgotPasswordUnified({ showAlert }) {
  const [activeTab, setActiveTab] = useState('email'); // email | sms | otp
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [logo, setLogo] = useState('');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    let ignore = false;
    const fetchLogo = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/logo`);
        if (!res.ok) return;
        const data = await res.json();
        if (!ignore && Array.isArray(data) && data[0]?.logo) setLogo(data[0].logo);
      } catch (_) {}
    };
    fetchLogo();
    return () => { ignore = true; };
  }, []);

  useEffect(() => { let t; if (cooldown>0) t = setTimeout(()=> setCooldown(cooldown-1),1000); return ()=> clearTimeout(t); }, [cooldown]);

  const sendOtp = async (type) => {
    setError('');
    if (!username.trim()) { setError('Please enter your username.'); return; }
    if (type === 'email' && !email.trim()) { setError('Please enter your email address.'); return; }
    if (type === 'sms' && !phone.trim()) { setError('Please enter your phone number.'); return; }
    setLoading(true);
    try {
      const payload = type === 'email' ? { type, email, username } : { type, phone, username };
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/usr-forgot-password`, { method: 'POST', headers: { 'Content-Type':'application/json','Accept':'application/json' }, body: JSON.stringify(payload) });
      const data = await res.json().catch(()=>({}));
      if (!res.ok) throw new Error(data?.message || 'Failed to send OTP.');
      showAlert?.(`OTP sent successfully. Please check your ${type}.`);
      try { localStorage.setItem('forgot_username', username.trim()); } catch(_) {}
      setActiveTab('otp');
    } catch (err) { setError(err?.message || 'Error sending OTP.'); } finally { setLoading(false); }
  };

  const passwordValid = PASSWORD_REGEX_LEGACY.test(newPassword);

  const changePassword = async () => {
    setError('');
    if (!otp.trim()) { setError('Please enter the OTP.'); return; }
    if (!passwordValid) { setError('Password must include letter, number & min 8 chars.'); return; }
    if (!confirmPassword.trim()) { setError('Please confirm your new password.'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (!username.trim()) { setError('Username missing. Go back.'); return; }
    setLoading(true);
    try {
      const payload = { otp: otp.trim(), password: newPassword, password_confirmation: confirmPassword, username: username.trim() };
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/usr-reset-password`, { method:'POST', headers:{'Content-Type':'application/json','Accept':'application/json'}, body: JSON.stringify(payload) });
      const data = await res.json().catch(()=>({}));
      if (!res.ok) throw new Error(data?.message || 'Failed to change password.');
      showAlert?.('Password changed successfully. Please login with your new password.');
      setTimeout(()=> (window.location.href='/login'), 1500);
    } catch (err) { setError(err?.message || 'Error during password change.'); } finally { setLoading(false); }
  };

  const resendOtp = async () => { if (cooldown>0) return; setCooldown(60); /* Optionally call sendOtp again if storing last type */ };

  return (
    <div className="fp-wrap" style={{ maxWidth: 480, margin: '0 auto' }}>
      <div className="fp-tabs">
        <button type="button" className={`fp-tab ${activeTab === 'email' ? 'active' : ''}`} onClick={() => setActiveTab('email')}>Email</button>
        <button type="button" className={`fp-tab ${activeTab === 'sms' ? 'active' : ''}`} onClick={() => setActiveTab('sms')}>SMS</button>
        <button type="button" className={`fp-tab ${activeTab === 'otp' ? 'active' : ''}`} disabled>OTP</button>
      </div>
      <h3 className="fp-title">Forgot password</h3>
      {logo && <div className="fp-logo" style={{ backgroundImage: `url('${process.env.REACT_APP_API_URL}/storage/${logo}')` }} />}
      {error && <div className="fp-error">{error}</div>}
      {activeTab !== 'otp' && (
        <form onSubmit={(e)=> { e.preventDefault(); sendOtp(activeTab); }} className="fp-form" noValidate>
          <label className="fp-label">Username</label>
          <input className="fp-input" value={username} onChange={(e)=> setUsername(e.target.value)} placeholder="Username" />
          <label className="fp-label">{activeTab === 'email' ? 'E-mail' : 'Phone Number'}</label>
          {activeTab === 'email' ? (
            <input className="fp-input" value={email} onChange={(e)=> setEmail(e.target.value)} placeholder="Input valid email" />
          ) : (
            <input className="fp-input" value={phone} onChange={(e)=> setPhone(e.target.value)} placeholder="Phone" />
          )}
          <button className="fp-submit" disabled={loading}>
            {loading && <span className="fp-spinner" />}
            {loading ? 'Sending...' : 'Send OTP'}
          </button>
          <div className="fp-note">OTP will be sent if account exists.</div>
        </form>
      )}
      {activeTab === 'otp' && (
        <form onSubmit={(e)=> { e.preventDefault(); changePassword(); }} className="fp-form" noValidate>
          <label className="fp-label">OTP Code</label>
          <input className="fp-input" value={otp} maxLength={6} onChange={(e)=> setOtp(e.target.value.replace(/\D/g,''))} placeholder="Enter 6 digit OTP" />
          <div style={{ display:'flex', gap:12, alignItems:'center', marginTop:4 }}>
            <button type="button" className="fp-submit" disabled={cooldown>0} onClick={resendOtp} style={{ padding:'6px 12px' }}>
              {cooldown>0 ? `Resend in ${cooldown}s` : 'Resend'}
            </button>
          </div>
          <label className="fp-label">New Password</label>
          <input className="fp-input" type="password" value={newPassword} onChange={(e)=> setNewPassword(e.target.value)} placeholder="Enter new password" />
          <label className="fp-label">Confirm Password</label>
          <input className="fp-input" type="password" value={confirmPassword} onChange={(e)=> setConfirmPassword(e.target.value)} placeholder="Confirm password" />
          <ul className="tips" style={{ margin:'8px 0', fontSize:12, color:'#999' }}>
            <li>Min 8 characters</li>
            <li>Include letter & number</li>
          </ul>
          <button className="fp-submit" disabled={loading || !passwordValid || newPassword !== confirmPassword || !otp}>
            {loading && <span className="fp-spinner" />}
            {loading ? 'Processing...' : 'Confirm'}
          </button>
        </form>
      )}
    </div>
  );
}
